import variables from '../../../common/styles/variables';
import { BaseComponent } from '../../base-component/base-component';
import { Dropdown } from 'bootstrap';
import { WishList } from '../interfaces/wish-list-items.interface';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import { WishListOptionsAnalytics } from '../utils/wish-list-options.analytics';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DownloadType } from '../../../common/utils/downloader/interfaces/downloader.interface';
import '../../product-catalog-download/product-catalog-download';
import { handleOptionDownload } from '../../product-catalog-download/utils/downloader.util';
import { openDownloadWishListOffCanvas, openShareWishListOffCanvas } from '../utils/wish-list-offcanvas.util';
import '../view/wish-list-create-off-canvas';
import '../view/wish-list-dowload-off-canvas';
import store from '../../../store/store';
import { connect } from 'pwa-helpers';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { StoreState } from '../../../store/store.interface';
import { selectUser } from '../../user-profile/user-profile.selectors';
import { mapWishListItemsToProducts } from '../utils/wish-list-product.util';
import { csvXlsxHeaders, productFields, wishListItemPdfHeader } from '../utils/wish-list-downloader.util';
import { COMMON_OFF_CANVAS_IDS } from '../../off-canvas/interfaces/common-off-canvas.interface';

const WISH_LIST_BUTTON_OPTIONS = {
  share_list: {
    id: 'wish-list-dropdown-share-option',
    data_automation_id: 'wish-list-share-btn',
    label: 'Share',
    aria_label: 'Share Button',
    icon: 'share',
  },
  edit_list_name: {
    id: 'wish-list-dropdown-edit-list-name-option',
    data_automation_id: 'wish-list-edit-list-name-btn',
    label: 'Edit List Name',
    aria_label: 'Edit List Name Button',
    icon: 'pencil',
  },
  download: {
    id: 'wish-list-dropdown-download-option',
    data_automation_id: 'wish-list-download-btn',
    label: 'Download',
    aria_label: 'Download Button',
    icon: 'download',
    data_bs_toggle: 'download-dropdown',
  },
  delete: {
    id: 'wish-list-dropdown-delete-option',
    data_automation_id: 'wish-list-delete-btn',
    label: 'Delete',
    aria_label: 'Delete Button',
    icon: 'trash',
  },
  download_pdf: {
    id: 'wish-list-dropdown-pdf-option',
    data_automation_id: 'wish-list-pdf-btn',
    label: 'Save as .PDF',
    aria_label: 'Save as .PDF Button',
  },
  download_csv: {
    id: 'wish-list-dropdown-csv-option',
    data_automation_id: 'wish-list-csv-btn',
    label: 'Save as .CSV',
    aria_label: 'Save as .CSV Button',
  },
  download_xlsx: {
    id: 'wish-list-dropdown-xlsx-option',
    data_automation_id: 'wish-list-xlsx-btn',
    label: 'Save as .XLSX',
    aria_label: 'Save as .XLSX Button',
  },
};

const SPACE_KEY_CODE = 32;

@customElement('mhecomm-wish-list-dropdown-options')
export class WishListDropdownOptions extends connect(store)(BaseComponent) {
  static styles = [
    variables,
    css`
      .dropdown-menu.options-dropdown,
      .dropdown-menu.dropdown-download-options {
        z-index: 1000 !important;
      }

      .dropdown-menu.dropdown-download-options {
        min-width: auto;
        white-space: nowrap;
      }

      [data-bs-toggle='dropdown'] {
        max-width: 2.5rem;
      }

      [data-bs-toggle='dropdown']:focus {
        border: 1px solid var(--mh-light-gray);
      }
    `,
  ];

  @property({ type: Object })
  wishList?: WishList;

  @property({ type: Boolean })
  iconView = false;

  @property({ type: Boolean })
  dropDownView = false;

  @property({ type: Array })
  dropDownItems?: string[] = [
    WishListDropdownConstants.SHARE,
    WishListDropdownConstants.EDIT_NAME,
    WishListDropdownConstants.DOWNLOAD,
    WishListDropdownConstants.DELETE,
  ];

  /**
   * If the component is used inside an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  @state()
  private user: UserProfile | undefined;

  downloadDropdownOptions = [
    WishListDropdownConstants.PDF,
    WishListDropdownConstants.CSV,
    WishListDropdownConstants.XLSX,
  ];
  private analytics: WishListOptionsAnalytics;

  private bsDropdown?: Dropdown;
  private dropdownElement?: HTMLElement;

  constructor() {
    super();
    this.analytics = new WishListOptionsAnalytics();
  }

  async stateChanged(state: StoreState) {
    this.user = selectUser(state);
  }

  renderComp() {
    if (!this.wishList) {
      console.error(`Unable to render dropdown options. wishList property not provided`);
      return nothing;
    }
    return html` ${this.dropDownView ? this.renderDropdownView() : this.iconView ? this.renderIconView() : nothing} `;
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    this.bsDropdown = this.bsInit('[data-bs-toggle="dropdown"]', window.bootstrap?.Dropdown)?.instance;
    this.dropdownElement = this.getDropdownMenu();
    if (this.bsDropdown) {
      this.bsDropdown?.hide();
    }
  }

  renderDropdownView() {
    return html`
      <div class="dropdown d-inline-block" role="group" part="dropdown">
        <button
          id="wish-list-${this.wishList?.wishlist_id}-options-btn"
          data-automation-id="wish-list-${this.wishList?.wishlist_id}-options-btn"
          type="button"
          class="btn btn-link text-reset"
          aria-expanded=${this.isDropdownExpanded(this.dropdownElement!)}
          aria-label="Wish List Dropdown Options"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
          title="Dropdown Options"
          @click=${(event: any) => this._handleDropdownToggle(event)}
        >
          ${this.uxtIconReady ? html` <uxt-icon icon="three-dots"></uxt-icon> ` : ''}
        </button>
        ${this.dropDownItems
          ? html` <ul
              id="wish-list-${this.wishList?.wishlist_id}-dropdown-option-dropdown-menu"
              aria-labelledby="wish-list-${this.wishList?.wishlist_id}-options-btn"
              class="dropdown-menu dropdown-menu-end options-dropdown"
              part="wish-list-dropdown-option-menu"
            >
              ${this.dropDownItems.map((dropDownItem) => {
                return html`
                  <li part="wish-list-dropdown-option-list">
                    <button
                      type="button"
                      id="${this.getButtonIdentifier(dropDownItem)}"
                      class="dropdown-item d-block btn btn-link"
                      data-automation-id="${this.getDataAutomationId(dropDownItem)}"
                      part="wish-list-dropdown-option-link"
                      aria-label="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].aria_label}"
                      @pointerdown=${(event: any) => this._optionSelection(event, dropDownItem)}
                    >
                      <span class="d-flex align-items-center">
                        ${this.uxtIconReady
                          ? html` <uxt-icon icon="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].icon}"></uxt-icon> `
                          : ''}
                        <span class="ms-2">${WISH_LIST_BUTTON_OPTIONS[dropDownItem].label}</span>
                      </span>
                    </button>
                  </li>
                `;
              })}
            </ul>`
          : nothing}
      </div>
      ${this.renderDownloadWishListOffCanvas()}
    `;
  }

  renderIconView() {
    return html`
      ${this.dropDownItems
        ? this.dropDownItems.map((dropDownItem) => {
            return html`
              <button
                type="button"
                id="${this.getButtonIdentifier(dropDownItem)}"
                class="btn btn-link text-reset ps-0"
                data-automation-id="${this.getDataAutomationId(dropDownItem)}"
                aria-label="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].aria_label}"
                ?data-bs-toggle="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].data_bs_toggle ? true : undefined}"
                data-bs-toggle="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].data_bs_toggle || ''}"
                @click=${(event: any) => this._optionSelection(event, dropDownItem)}
              >
                ${this.uxtIconReady && WISH_LIST_BUTTON_OPTIONS[dropDownItem].icon !== ''
                  ? html` <uxt-icon icon="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].icon}" class="me-2"></uxt-icon> `
                  : ''}
              </button>
              <ul
                id="wish-list-${this.wishList?.wishlist_id}-download-option-dropdown-menu"
                aria-labelledby="wish-list-${this.wishList?.wishlist_id}-download-options-btn"
                class="dropdown-menu dropdown-menu-end dropdown-download-options"
                part="wish-list-dropdown-download-option-menu"
              >
                ${this.downloadDropdownOptions.map((dropDownItem) => {
                  return html`
                    <li part="wish-list-download-dropdown-option-list">
                      <button
                        type="button"
                        id="${this.getButtonIdentifier(dropDownItem)}"
                        class="dropdown-item d-block btn btn-link"
                        data-automation-id="${this.getDataAutomationId(dropDownItem)}"
                        part="wish-list-download-dropdown-option-link"
                        aria-label="${WISH_LIST_BUTTON_OPTIONS[dropDownItem].aria_label}"
                        @pointerdown=${(event: any) => this._optionSelection(event, dropDownItem)}
                      >
                        <span class="d-flex align-items-center">
                          ${WISH_LIST_BUTTON_OPTIONS[dropDownItem].label}
                        </span>
                      </button>
                    </li>
                  `;
                })}
              </ul>
            `;
          })
        : nothing}
    `;
  }

  renderDownloadWishListOffCanvas() {
    return html`
      <div>
        <mhecomm-wish-list-download-off-canvas> </mhecomm-wish-list-download-off-canvas>
      </div>
    `;
  }

  private getButtonIdentifier(item) {
    return WISH_LIST_BUTTON_OPTIONS[item]?.id + '-' + this.wishList?.wishlist_id;
  }

  private _handleDropdownToggle(event): void {
    if (event && (event.type === 'click' || (event.type === 'keyup' && event.which === SPACE_KEY_CODE))) {
      this.bsDropdown?.toggle();
      this.analytics.dropdownToggle('wish-list-dropdown', this, this.isDropdownExpanded(this.dropdownElement!));

      this.addEventListener('focusout', (): void => {
        this.bsDropdown?.hide();
      });
    }
  }

  private _optionSelection(event, option: string): void {
    if ('ontouchstart' in window) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.wishList) {
      return;
    }

    // to trigger the event listener
    this.analytics.optionSelection(
      'wish-list', 
      this, 
      option, 
      this.wishList, 
      this.offCanvasId
    );
    // to emit event and push to datalayer
    this.analytics.optionSelectionEvent(
      'wish-list', 
      this, 
      option, 
      this.wishList, 
      this.getButtonIdentifier(option)
    );

    switch (option) {
      case WishListDropdownConstants.SHARE:
        if (this.offCanvasId === COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK) {
          openShareWishListOffCanvas(this.wishList.wishlist_id);
        }
        break;

      case WishListDropdownConstants.DOWNLOAD:
        if (this.offCanvasId) {
          break;
        }

        this.bsDropdown = this.bsInit('[data-bs-toggle="download-dropdown"]', window.bootstrap?.Dropdown)?.instance;
        if (!this.bsDropdown) {
          openDownloadWishListOffCanvas(this.wishList);
          this.bsDropdown = this.bsInit('[data-bs-toggle="dropdown"]', window.bootstrap?.Dropdown)?.instance;
        }
        this._handleDropdownToggle(event);
        break;

      case WishListDropdownConstants.PDF:
        handleOptionDownload(
          DownloadType.PDF,
          this.wishList.name,
          wishListItemPdfHeader(this.wishList),
          csvXlsxHeaders,
          productFields,
          mapWishListItemsToProducts(this.wishList.wishlist_items, `${this.user?.givenName} ${this.user?.familyName}`),
          true
        );
        break;

      case WishListDropdownConstants.CSV:
        handleOptionDownload(
          DownloadType.CSV,
          this.wishList?.name!,
          wishListItemPdfHeader(this.wishList),
          csvXlsxHeaders,
          productFields,
          mapWishListItemsToProducts(this.wishList.wishlist_items, `${this.user?.givenName} ${this.user?.familyName}`),
          true
        );
        break;

      case WishListDropdownConstants.XLSX:
        handleOptionDownload(
          DownloadType.XLSX,
          this.wishList.name,
          wishListItemPdfHeader(this.wishList),
          csvXlsxHeaders,
          productFields,
          mapWishListItemsToProducts(this.wishList.wishlist_items, `${this.user?.givenName} ${this.user?.familyName}`),
          true
        );
        break;

      default:
        console.warn(`Unknown button selected: ${option}`);
        break;
    }
  }

  private getDropdownMenu(): HTMLElement {
    return this.shadowRoot!.querySelector(`#wish-list-dropdown-option-dropdown-menu`) as HTMLElement;
  }

  private getDataAutomationId(dropDownItem: string) {
    return `${WISH_LIST_BUTTON_OPTIONS[dropDownItem].data_automation_id}-${this.wishList!.wishlist_id}`;
  }

  private isDropdownExpanded(dropdown: HTMLElement) {
    return dropdown?.classList?.contains('show') ? true : false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-dropdown-options': WishListDropdownOptions;
  }
}
